import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Globe, TrendingUp, Users, Zap, MapPin, BarChart3, Shield, Clock } from 'lucide-react';

const AfricanBusiness = () => {
  const { language } = useLanguage();

  const africanMarketStats = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "1.4B+ Population",
      description: "Fastest growing market globally"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "60% GDP Growth",
      description: "By 2030 with digital adoption"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "54 Countries",
      description: "Diverse markets, unified opportunities"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "70% Mobile First",
      description: "Leading mobile-first innovation"
    }
  ];

  const successStories = [
    {
      country: "Nigeria",
      company: "FinTech Solutions Ltd",
      industry: "Financial Services",
      challenge: "Manual loan processing taking 30+ days",
      solution: "AI-powered document automation & risk assessment",
      result: "Loan approval reduced to 2 hours, 300% increase in applications",
      savings: "$4.2M annually"
    },
    {
      country: "Kenya",
      company: "LogiFlow Africa",
      industry: "Logistics",
      challenge: "Inefficient route planning and tracking",
      solution: "AI route optimization & real-time tracking",
      result: "35% reduction in delivery times, 50% fuel savings",
      savings: "$2.8M annually"
    },
    {
      country: "South Africa",
      company: "MediCare Plus",
      industry: "Healthcare",
      challenge: "Patient data management and diagnosis delays",
      solution: "AI-assisted diagnosis & automated patient workflows",
      result: "40% faster diagnosis, 60% improved patient satisfaction",
      savings: "$3.5M annually"
    },
    {
      country: "Ghana",
      company: "AgriTech Innovations",
      industry: "Agriculture",
      challenge: "Crop yield prediction and resource optimization",
      solution: "AI-powered agricultural analytics & automation",
      result: "25% increase in crop yields, 40% water savings",
      savings: "$1.9M annually"
    }
  ];

  const challenges = [
    {
      title: "Infrastructure Limitations",
      description: "Limited connectivity and power infrastructure",
      solution: "Edge computing and offline-capable AI solutions"
    },
    {
      title: "Skills Gap",
      description: "Shortage of technical expertise",
      solution: "Comprehensive training programs and knowledge transfer"
    },
    {
      title: "Budget Constraints",
      description: "Limited capital for technology investments",
      solution: "Flexible pricing and phased implementation approach"
    },
    {
      title: "Regulatory Compliance",
      description: "Varying regulations across countries",
      solution: "Localized compliance frameworks and expertise"
    }
  ];

  const services = [
    {
      title: "Mobile-First AI Solutions",
      description: "Optimized for African mobile infrastructure",
      features: ["Offline capabilities", "Low bandwidth optimization", "SMS integration", "USSD support"]
    },
    {
      title: "Localized Language Support",
      description: "AI that understands African languages and contexts",
      features: ["Swahili", "Amharic", "Hausa", "Yoruba", "Zulu", "French", "Arabic"]
    },
    {
      title: "Affordable Implementation",
      description: "Flexible pricing for emerging markets",
      features: ["Pay-as-you-grow", "Minimal upfront costs", "Local partnerships", "Grant support"]
    },
    {
      title: "Cultural Adaptation",
      description: "Solutions designed for African business practices",
      features: ["Local customs integration", "Community-based models", "Traditional payment methods", "Cultural sensitivity"]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-orange-500/5 via-background to-green-500/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <MapPin className="w-4 h-4 mr-2" />
              Empowering African Businesses
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-orange-600 to-green-600 bg-clip-text text-transparent">
              Digital Transformation for African Businesses
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto mb-8">
              Purpose-built AI solutions for the unique challenges and opportunities of African markets. 
              From Lagos to Nairobi, Cairo to Cape Town - we're accelerating digital transformation across the continent.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-orange-600 hover:bg-orange-700">
                Start Your Digital Journey
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button size="lg" variant="outline">
                Explore Success Stories
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Market Stats */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Africa: The World's Next Digital Powerhouse
            </h2>
            <p className="text-xl text-muted-foreground">
              Why smart businesses are investing in African digital transformation now
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {africanMarketStats.map((stat, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="mx-auto mb-4 p-3 bg-orange-500/10 rounded-full w-fit">
                    {stat.icon}
                  </div>
                  <CardTitle className="text-lg">{stat.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{stat.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Transforming African Businesses
            </h2>
            <p className="text-xl text-muted-foreground">
              Real results from companies across the continent
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {successStories.map((story, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <Badge variant="outline" className="mb-2">{story.country}</Badge>
                    <h3 className="text-lg font-semibold">{story.company}</h3>
                    <p className="text-sm text-muted-foreground">{story.industry}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">{story.savings}</div>
                    <div className="text-sm text-muted-foreground">Annual Savings</div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-red-600">Challenge:</span>
                    <p className="text-sm text-muted-foreground">{story.challenge}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-blue-600">Solution:</span>
                    <p className="text-sm text-muted-foreground">{story.solution}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-green-600">Result:</span>
                    <p className="text-sm text-muted-foreground">{story.result}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Overcoming African Market Challenges
            </h2>
            <p className="text-xl text-muted-foreground">
              How we address the unique challenges of doing business in Africa
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {challenges.map((challenge, index) => (
              <Card key={index} className="p-6">
                <h3 className="text-lg font-semibold mb-2">{challenge.title}</h3>
                <p className="text-muted-foreground mb-3">{challenge.description}</p>
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-green-600 font-medium">{challenge.solution}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized Services */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Services Designed for Africa
            </h2>
            <p className="text-xl text-muted-foreground">
              Tailored solutions that work in African environments
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                <p className="text-muted-foreground mb-4">{service.description}</p>
                <div className="grid grid-cols-2 gap-2">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-orange-600 rounded-full"></div>
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Timeline */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Fast Implementation, Real Results
            </h2>
            <p className="text-xl text-muted-foreground">
              Our proven approach to digital transformation in African markets
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-lg font-semibold mb-2">Market Assessment</h3>
              <p className="text-sm text-muted-foreground">Local market analysis and opportunity identification</p>
              <div className="text-sm text-orange-600 font-medium mt-2">Week 1</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-lg font-semibold mb-2">Cultural Adaptation</h3>
              <p className="text-sm text-muted-foreground">Customize solutions for local business practices</p>
              <div className="text-sm text-orange-600 font-medium mt-2">Week 2-3</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-lg font-semibold mb-2">Phased Rollout</h3>
              <p className="text-sm text-muted-foreground">Gradual implementation with minimal disruption</p>
              <div className="text-sm text-orange-600 font-medium mt-2">Week 4-8</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
              <h3 className="text-lg font-semibold mb-2">Local Support</h3>
              <p className="text-sm text-muted-foreground">Ongoing support with local teams and partners</p>
              <div className="text-sm text-orange-600 font-medium mt-2">Ongoing</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-orange-600 to-green-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Transform Your African Business?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join 200+ African companies that have accelerated their growth with our digital solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary">
              Get Free Market Assessment
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-orange-600">
              Download Africa Digital Report
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AfricanBusiness;